/**
 * @file     CompareContext.tsx
 * @purpose  Cross-page compare tray state. Holds up to 4 properties selected
 *           for side-by-side comparison. Persisted to sessionStorage so state
 *           survives navigation within a tab but clears on tab close.
 */
'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { getSessionStorage } from '@/core/utils/browserRuntime';
import { readStorageEnvelope, writeStorageEnvelope } from '@/core/utils/storageCache';

export const COMPARE_MAX = 4;
/** Desktop tray height in px. The tray pins this via minHeight and the map's
 *  hover-card placement subtracts it from the usable map bottom, so the two
 *  can't drift apart. Tray shows at >= 2 items (see CompareTray `visible`). */
export const COMPARE_TRAY_DESKTOP_H = 80;
const STORAGE_KEY = 'relta_compare';
const STORAGE_VERSION = 1;

export interface CompareItem {
  slug: string;
  title: string;
  price: number | null;
  thumbSrc: string | null;
}

const isCompareItemArray = (v: unknown): v is CompareItem[] =>
  Array.isArray(v) &&
  v.every(
    (i) =>
      i !== null &&
      typeof i === 'object' &&
      typeof (i as CompareItem).slug === 'string',
  );

interface CompareContextValue {
  items: CompareItem[];
  add: (item: CompareItem) => void;
  remove: (slug: string) => void;
  toggle: (item: CompareItem) => void;
  clear: () => void;
  has: (slug: string) => boolean;
  isFull: boolean;
}

const CompareContext = createContext<CompareContextValue | null>(null);

export function CompareProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CompareItem[]>(() => {
    const storage = getSessionStorage();
    return (
      readStorageEnvelope({
        key: STORAGE_KEY,
        storage,
        version: STORAGE_VERSION,
        validate: isCompareItemArray,
      }) ?? []
    );
  });

  useEffect(() => {
    const storage = getSessionStorage();
    writeStorageEnvelope({ key: STORAGE_KEY, storage, value: items, version: STORAGE_VERSION });
  }, [items]);

  const add = useCallback((item: CompareItem) => {
    setItems((prev) => {
      if (prev.some((i) => i.slug === item.slug)) return prev;
      if (prev.length >= COMPARE_MAX) return prev;
      return [...prev, item];
    });
  }, []);

  const remove = useCallback((slug: string) => {
    setItems((prev) => prev.filter((i) => i.slug !== slug));
  }, []);

  const toggle = useCallback((item: CompareItem) => {
    setItems((prev) => {
      if (prev.some((i) => i.slug === item.slug)) {
        return prev.filter((i) => i.slug !== item.slug);
      }
      if (prev.length >= COMPARE_MAX) return prev;
      return [...prev, item];
    });
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const has = useCallback((slug: string) => items.some((i) => i.slug === slug), [items]);

  const value = useMemo(
    () => ({ items, add, remove, toggle, clear, has, isFull: items.length >= COMPARE_MAX }),
    [items, add, remove, toggle, clear, has],
  );

  return <CompareContext.Provider value={value}>{children}</CompareContext.Provider>;
}

const NOOP_CONTEXT: CompareContextValue = {
  items: [],
  add: () => {},
  remove: () => {},
  toggle: () => {},
  clear: () => {},
  has: () => false,
  isFull: false,
};

export function useCompare(): CompareContextValue {
  return useContext(CompareContext) ?? NOOP_CONTEXT;
}
